import { motion } from "framer-motion";
import { Sunrise, Sunset, Scale, Ban, AlertTriangle, Clock } from "lucide-react";

const card = "rounded-3xl border border-white/8 bg-white/[0.03] p-8 transition-colors duration-500 hover:border-terracotta/40";

export default function Rules() {
  return (
    <section id="service-rules" data-testid="service-rules-section" className="relative py-28 sm:py-36 bg-ink/40">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-terracotta mb-5"
        >
          Service rules & guidelines
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl leading-tight"
        >
          Two windows. One standard: <span className="text-terracotta">immaculate.</span>
        </motion.h2>

        <div className="mt-16 grid md:grid-cols-4 gap-5">
          <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            className={`${card} md:col-span-2`} data-testid="rule-morning-window">
            <div className="flex items-start justify-between">
              <Sunrise className="w-8 h-8 text-gold" strokeWidth={1.6} />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim">Window A</span>
            </div>
            <p className="mt-10 font-display font-extrabold text-3xl sm:text-4xl text-parchment">6:00 AM – 9:00 AM</p>
            <p className="mt-3 text-sm text-mist">Morning window. Bags must be outside your door within these three hours.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
            className={`${card} md:col-span-2`} data-testid="rule-evening-window">
            <div className="flex items-start justify-between">
              <Sunset className="w-8 h-8 text-terracotta" strokeWidth={1.6} />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim">Window B</span>
            </div>
            <p className="mt-10 font-display font-extrabold text-3xl sm:text-4xl text-parchment">6:00 PM – 9:00 PM</p>
            <p className="mt-3 text-sm text-mist">Evening window. Perfect for after-dinner disposal before the night sweep.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}
            className={card} data-testid="rule-weight-limit">
            <Scale className="w-8 h-8 text-parchment" strokeWidth={1.6} />
            <p className="mt-8 font-display font-extrabold text-4xl text-gold">25 lbs</p>
            <p className="mt-3 text-sm text-mist">Maximum weight per bag. Split heavier loads into multiple tied bags.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}
            className={card} data-testid="rule-prohibited">
            <Ban className="w-8 h-8 text-terracotta" strokeWidth={1.6} />
            <p className="mt-8 font-display font-bold text-xl">Not Accepted</p>
            <ul className="mt-3 text-sm text-mist space-y-1.5">
              <li>— Hazardous materials</li>
              <li>— Furniture & bulky items</li>
              <li>— Loose, untied waste</li>
            </ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.25 }}
            className={`${card} md:col-span-2 border-terracotta/30 bg-terracotta/[0.06]`} data-testid="rule-strict-window">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-terracotta shrink-0" strokeWidth={1.6} />
              <div>
                <p className="font-display font-bold text-xl text-parchment">Strict hallway policy</p>
                <p className="mt-2 text-sm text-mist leading-relaxed">
                  No trash may be left in hallways outside the designated 3-hour windows.
                  Items left out early or late are subject to building fines — set a reminder, we've got the rest.
                </p>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-2 text-dim">
              <Clock className="w-3.5 h-3.5" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Enforced daily, both windows</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
